import React from 'react'
import PropTypes from 'prop-types'
import {Flex, Text, Button, useMultiStyleConfig, useMediaQuery} from '@chakra-ui/react'
import CustomLink from '../../../custom-link'
import {TextTitleDescription} from '../../text-title-description'
import {remCalc} from '../../../../theme/_utils'
import {ChevronRight} from '../../../icons'
import {useGetCategoriesData} from '../../../../custom-api/hooks/use-get-categories-data'
import {safeBuildUrl, getGlobalLink} from '../../../../utils/contentstack-utils'
import {
    getDamImagePicture,
    createCompleteImageSet,
    getAspectRatioObjectFromImageSet,
} from '../../../../custom-hooks/use-dam-image'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useProducts} from '@salesforce/commerce-sdk-react'

const MoodBoardCard = ({textBlock, images, cta}) => {
    const categoriesData = useGetCategoriesData()
    const {data: productsData} = useProducts(
        {
            parameters: {
                ids: cta?.productsfcc?.data?.length > 0 ? cta.productsfcc.data[0].id : '',
                expand: ['availability', 'images'],
            },
            headers: {
                'x-debug': 'moodboard-index',
            },
        },
        {
            enabled: cta?.productsfcc?.data?.length > 0,
        },
    )
    const {buildUrl, locale} = useMultiSite()

    const {url, label, isAbsolute} = getGlobalLink({
        link: cta,
        categoriesData,
        productsData,
        locale: locale?.alias || locale?.id?.toLowerCase(),
    })
    const [isSmallTablet] = useMediaQuery(
        `(min-width: ${remCalc(450)}) and (max-width: ${remCalc(768)})`,
    )

    const styles = useMultiStyleConfig('Moodboard', {
        isSmallTablet,
    })

    const imageSet = createCompleteImageSet(images)
    const ratio = getAspectRatioObjectFromImageSet(imageSet) || {base: 16 / 9}

    return (
        <CustomLink
            isExternal={isAbsolute}
            href={isAbsolute ? url : safeBuildUrl(url, buildUrl)}
            _hover={{textDecoration: 'none'}}
            data-testid="moodboard-card-link"
            suppressHydrationWarning
        >
            <Flex
                {...styles.cardContainer}
                position="relative"
                inset={0}
                zIndex={0}
                data-testid="moodboard-container"
            >
                {getDamImagePicture(
                    imageSet,
                    textBlock?.title || '',
                    false,
                    ratio,
                    {objectFit: 'cover'},
                    'linear-gradient(180deg, rgba(0, 0, 0, 0.00) 0%, rgba(0, 0, 0, 0.50) 100%)',
                )}
                <Flex
                    {...styles.textContentContainer}
                    position="absolute"
                    inset={0}
                    data-testid="moodboard-text-container"
                    zIndex={2}
                >
                    <TextTitleDescription
                        title={textBlock?.title}
                        {...styles.moodboardTextTitleDescription}
                        tabIndex={0}
                    >
                        {textBlock?.body_copy}
                    </TextTitleDescription>
                    {label && (
                        <Button
                            {...styles.moodboardButton}
                            aria-label={label}
                            tabIndex={0}
                            rightIcon={
                                <ChevronRight aria-hidden="true" {...styles.moodboardChevronIcon} />
                            }
                        >
                            <Text as="span" {...styles.moodboardButtonText}>
                                {label}
                            </Text>
                        </Button>
                    )}
                </Flex>
            </Flex>
        </CustomLink>
    )
}

MoodBoardCard.propTypes = {
    textBlock: PropTypes.shape({
        title: PropTypes.string,
        body_copy: PropTypes.string,
    }),
    images: PropTypes.object,
    cta: PropTypes.shape(),
}

export default MoodBoardCard
